/* global bocAmchart4, am4core, chartLanguage */

/**
 * @typedef {Object<
 *     am4charts.XYChart,
 *     {
 *          bocSettings: {
 *              series,
 *              dataProcessor,
 *              addExport,
 *              lookup,
 *              args
 *          },
 *          events,
 *          exporting,
 *          legend,
 *          dataSource,
 *          responsive
 *     }
 * >} AMConfig
 */

/*
 * Polyfill for endsWith method not supported by IE
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith#Polyfill
 */
if (!String.prototype.endsWith) {
    String.prototype.endsWith = function (searchString, position) {
        let subjectString = this.toString();
        if (typeof position !== 'number' || !isFinite(position)
            || Math.floor(position) !== position || position > subjectString.length) {
            position = subjectString.length;
        }
        position -= searchString.length;
        let lastIndex = subjectString.indexOf(searchString, position);
        return lastIndex !== -1 && lastIndex === position;
    };
}

/**
 * Apply BOC settings to a chart.
 * @param {AMConfig} chartSettings The chart options
 * @returns {AMConfig} Modified chart options
 */
bocAmchart4.applyBocSettings = function (chartSettings) {
    if (bocAmchart4.debug(chartSettings)) {
        // eslint-disable-next-line no-console
        console.log(
            chartSettings.bocSettings.id,
            "Running applyBosSettings:",
            bocAmchart4.debugClone(chartSettings)
        );
    }

    if (document.documentElement.getAttribute('data-theme') === 'dark') {
        chartSettings.theme = 'bocDark';
        bocAmchart4.theme = 'bocDark';
        bocAmchart4.setTheme('bocDark');
    } else {
        chartSettings.theme = 'boc';
        bocAmchart4.setTheme('boc');
    }
    chartSettings.bocSettings.theme = chartSettings.theme;
    am4core.options.commercialLicense = true;
    chartSettings.fontSize = "0.875em";
    if (chartLanguage === "fr") {
        chartSettings.numberFormatter = {
            "language": {
                "type": "Language",
                "locale": {
                    "_decimalSeparator": ",",
                    "_thousandSeparator": " "
                }
            }
        };
    }

    chartSettings.responsive = chartSettings.responsive || {"enabled": false};
    if (chartSettings.bocSettings.args.hasOwnProperty("responsive")) {
        chartSettings.responsive = {"enabled": chartSettings.bocSettings.args.responsive === 'true'};
    }

    ['Left', 'Right', 'Top', 'Bottom'].forEach(direction => bocAmchart4.addPadding(chartSettings, direction));
    bocAmchart4.setTitlesFromShortcode(chartSettings);
    bocAmchart4.setNotesFromShortcode(chartSettings);

    bocAmchart4.remPixels = parseInt(window.getComputedStyle(document.body).fontSize);

    // Setting the base font property to force print preview to be based on 16px
    document.getElementById(chartSettings.bocSettings.id).style.fontSize = "16px";
    document.getElementById(chartSettings.bocSettings.id + "-titles").style.fontSize = "16px";
    document.getElementById(chartSettings.bocSettings.id + "-legend").style.fontSize = "16px";
    document.getElementById(chartSettings.bocSettings.id + "-notes").style.fontSize = "16px";


    if (chartSettings.bocSettings.hasOwnProperty('type')) {
        switch (chartSettings.bocSettings.type) {
            case 'pie':
                break;
            case 'map':
                if (chartSettings.bocSettings.args.hasOwnProperty("map")) {
                    let objectName = 'am4geodata_' + chartSettings.bocSettings.args.map;
                    objectName = objectName.replaceAll("/", "_");
                    if (typeof window[objectName] === 'object') {
                        chartSettings.geodata = window[objectName];
                    }
                    // disable scroll wheel zoom to avoid page scroll interference
                    chartSettings.chartContainer = chartSettings.chartContainer ? chartSettings.chartContainer : {};
                    chartSettings.chartContainer.wheelable = false;
                }
                break;
            case 'xy': {
                let xAxisType = "date";
                if (chartSettings.bocSettings.args.hasOwnProperty("xaxistype")) {
                    xAxisType = chartSettings.bocSettings.args.xaxistype;
                }

                switch (xAxisType) {
                    case 'date':
                        // time by default
                        chartSettings.xAxes = [{
                            "id": "bottom",
                            "type": "DateAxis",
                            "dataFields": {
                                "date": "date"
                            },
                            "renderer": {
                                "cellStartLocation": 0.03,
                                "cellEndLocation": 0.97,
                                "minGridDistance": 3.75 * bocAmchart4.remPixels,
                                "labels": {
                                    "template": {
                                        "fontSize": "0.914em"
                                    }
                                }
                            },
                            "events": {
                                "rangechangestarted": function (ev) {
                                    let axis = ev.target;
                                    if (chartSettings.bocSettings.args.hasOwnProperty('centerdatelabels')
                                        && chartSettings.bocSettings.args['centerdatelabels'] === "true") {
                                        axis.renderer.labels.template.horizontalCenter = "middle";
                                        axis.renderer.labels.template.location = 0.5;
                                    } else {
                                        axis.renderer.labels.template.horizontalCenter = "left";
                                        axis.renderer.labels.template.location = 0.0001;
                                    }
                                    axis.renderer.maxLabelPosition = 0.97;
                                }
                            }
                        }];
                        if (chartSettings.bocSettings.args.hasOwnProperty('datecrop')) {
                            let cropParam = chartSettings.bocSettings.args.datecrop;
                            let yearParam = cropParam.slice(0, -1);
                            if (cropParam.slice(-1) === "Y" && parseInt(yearParam).toString() === yearParam) {
                                let endYear;
                                if (typeof window.enddate !== 'undefined' && window.enddate !== '') {
                                    endYear = new Date(window.enddate).getFullYear();
                                } else {
                                    endYear = new Date().getFullYear();
                                }
                                let cropStartDate = (endYear - yearParam) + "-01-01";
                                bocAmchart4.initChartVars(chartSettings);
                                bocAmchart4.vars[chartSettings.bocSettings.id]['startdateOverride'] = cropStartDate;
                            }
                        }
                        break;
                    case 'time':
                        // time by default
                        chartSettings.xAxes = [{
                            "id": "bottom",
                            "type": "DurationAxis",
                            "baseUnit": "second",
                            "dataFields": {
                                "valueX": "date"
                            },
                            "renderer": {
                                "cellStartLocation": 0.03,
                                "cellEndLocation": 0.97,
                                "maxLabelPosition": 0.97,
                                "minGridDistance": 3.75 * bocAmchart4.remPixels,
                                "labels": {
                                    "template": {
                                        "fontSize": "0.914em"
                                    }
                                }
                            }
                        }];
                        break;
                    case 'number':
                        chartSettings.xAxes = [{
                            "id": "bottom",
                            "type": "ValueAxis",
                            "dataFields": {},
                            "renderer": {
                                "cellStartLocation": 0.03,
                                "cellEndLocation": 0.97,
                                "labels": {
                                    "template": {
                                        "fontSize": "0.914em"
                                    }
                                }
                            },
                            // Enable tooltips for ValueAxis charts
                            // Source: https://www.amcharts.com/docs/v4/tutorials/multiple-cursor-tooltips-on-scatter-chart/
                            "getSeriesDataItem": function (series, position) {
                                let key = this.axisFieldName + this.axisLetter;
                                let value = this.positionToValue(position);
                                return series.dataItems.getIndex(series.dataItems.findClosestIndex(value, function (xCoord) {
                                    return xCoord[key];
                                }, "any"));
                            }
                        }];
                        break;
                    case 'text':
                        chartSettings.xAxes = [{
                            "type": "CategoryAxis",
                            "id": "bottom",
                            "dataFields": {
                                "category": "category"
                            },
                            "renderer": {
                                "cellStartLocation": 0.03,
                                "cellEndLocation": 0.97,
                                "minGridDistance": 3.75 * bocAmchart4.remPixels,
                                "grid": {
                                    "template": {
                                        "location": 0
                                    }
                                },
                                "labels": {
                                    "template": {
                                        "fontSize": "0.914em"
                                    }
                                }
                            },
                            "events": {
                                "datarangechanged": function (ev) {
                                    let axis = ev.target;
                                    setTimeout(function () {
                                        let labelMaxWidth;
                                        if (chartSettings.bocSettings.args.hasOwnProperty("categoryaxislabelmaxwidth")
                                            && !isNaN(chartSettings.bocSettings.args['categoryaxislabelmaxwidth'])) {
                                            labelMaxWidth = parseInt(chartSettings.bocSettings.args['categoryaxislabelmaxwidth']);
                                        } else if (chartSettings.bocSettings.args.hasOwnProperty("bottomaxislabelmaxwidth")) {
                                            labelMaxWidth = parseInt(chartSettings.bocSettings.args['bottomaxislabelmaxwidth']);
                                        } else {
                                            labelMaxWidth = 200;
                                        }

                                        let gapLimit = 5.625 * 16;
                                        if (chartSettings.bocSettings.args.hasOwnProperty("bottomaxisgaplimit")
                                            && !isNaN(chartSettings.bocSettings.args['bottomaxisgaplimit'])) {
                                            gapLimit = parseInt(chartSettings.bocSettings.args['bottomaxisgaplimit']);
                                        }

                                        bocAmchart4.initChartVars(chartSettings);
                                        if (bocAmchart4.vars[chartSettings.bocSettings.id].hasOwnProperty('printDone')) {
                                            if (!bocAmchart4.vars[chartSettings.bocSettings.id].hasOwnProperty('afterPrintDone')) {
                                                labelMaxWidth = labelMaxWidth / 16 * bocAmchart4.remPixels;
                                                gapLimit = gapLimit / 16 * bocAmchart4.remPixels;
                                                bocAmchart4.vars[chartSettings.bocSettings.id].afterPrintDone = true;
                                            }
                                        } else {
                                            bocAmchart4.vars[chartSettings.bocSettings.id].printDone = true;
                                        }

                                        let gap = axis.pixelWidth / (axis.endIndex - axis.startIndex);
                                        axis.renderer.labels.template.wrap = true;
                                        axis.renderer.labels.template.truncate = false;
                                        axis.renderer.labels.template.textAlign = "middle";

                                        axis.renderer.labels.each(function (label) {
                                            if (gap < gapLimit) {
                                                label.rotation = -90;
                                                label.horizontalCenter = "right";
                                                label.verticalCenter = "middle";
                                                label.textAlign = "end";
                                                label.maxWidth = labelMaxWidth;
                                                label.marginBottom = 4;
                                                label.paddingBottom = 0;
                                                label.paddingTop = 0;
                                                label.inside = false;
                                            } else {
                                                label.rotation = 0;
                                                label.horizontalCenter = "middle";
                                                label.verticalCenter = "middle";
                                                label.textAlign = "middle";
                                                label.maxWidth = gap;
                                                label.marginBottom = 8;
                                                label.paddingBottom = 4;
                                                label.paddingTop = 8;
                                                label.inside = false;
                                            }
                                        });

                                    }, 1);
                                    if (chartSettings.bocSettings.args.hasOwnProperty("multilevel")) {
                                        let separator = "|";
                                        if (chartSettings.bocSettings.args.hasOwnProperty('multilevelseparator')) {
                                            separator = chartSettings.bocSettings.args.multilevelseparator;
                                        }
                                        axis.renderer.labels.template.adapter.add("textOutput", function (text) {
                                            if (typeof text === "string" && text.indexOf(separator) !== -1) {
                                                return text.split(separator)[1];
                                            } else {
                                                return text;
                                            }
                                        });
                                    }
                                }
                            }
                        }];
                        break;
                }
                chartSettings.cursor = {
                    "type": "XYCursor",
                    "behavior": "zoomX"
                };

                let yAxisType = "number";
                if (chartSettings.bocSettings.args.hasOwnProperty("yaxistype")) {
                    yAxisType = chartSettings.bocSettings.args.yaxistype;
                }
                switch (yAxisType) {
                    case 'text': {
                        let labelMaxWidth = 300;
                        if (chartSettings.bocSettings.args.hasOwnProperty("categoryaxislabelmaxwidth")) {
                            labelMaxWidth = parseInt(chartSettings.bocSettings.args['categoryaxislabelmaxwidth']);
                        } else if (chartSettings.bocSettings.args.hasOwnProperty("leftaxislabelmaxwidth")) {
                            labelMaxWidth = parseInt(chartSettings.bocSettings.args['leftaxislabelmaxwidth']);
                        }

                        labelMaxWidth = labelMaxWidth / 16 * bocAmchart4.remPixels;
                        chartSettings.yAxes = [{
                            "type": "CategoryAxis",
                            "id": "left",
                            "dataFields": {
                                "category": "category"
                            },
                            "renderer": {
                                "cellStartLocation": 0.03,
                                "cellEndLocation": 0.97,
                                "minGridDistance": 1.25 * bocAmchart4.remPixels,
                                "inversed": true,
                                "labels": {
                                    "wrap": true,
                                    "align": "right",
                                    "template": {
                                        "textAlign": "end",
                                        "fontSize": "0.914em"
                                    }
                                },
                                "grid": {
                                    "template": {
                                        "location": 0
                                    }
                                }
                            }
                        }];

                        chartSettings.events = chartSettings.events || {};
                        chartSettings.events.sizechanged = function (ev) {
                            bocAmchart4.applyLabelWrapping(labelMaxWidth, ev.target);
                        };

                        chartSettings.events.validated = function (ev) {
                            bocAmchart4.applyLabelWrapping(labelMaxWidth, ev.target);
                        };
                        chartSettings.events.datavalidated = function (ev) {
                            if (chartSettings.bocSettings.args.hasOwnProperty("multilevel")) {
                                let separator = "|";
                                if (chartSettings.bocSettings.args.hasOwnProperty('multilevelseparator')) {
                                    separator = chartSettings.bocSettings.args.multilevelseparator;
                                }
                                let axis = ev.target.yAxes.getIndex(0);
                                axis.renderer.labels.template.adapter.add("textOutput", function (text) {
                                    if (typeof text === "string" && text.indexOf(separator) !== -1) {
                                        return text.split(separator)[1];
                                    } else {
                                        return text;
                                    }
                                });
                                bocAmchart4.applyLabelWrapping(labelMaxWidth, ev.target);
                            }
                        };

                        chartSettings.cursor = {
                            "type": "XYCursor",
                            "behavior": ""
                        };
                        break;
                    }
                    default:
                        chartSettings.yAxes[0].renderer.labels = {
                            "template": {
                                "fontSize": "0.914em"
                            }
                        };
                        chartSettings.yAxes[1].renderer.labels = {
                            "template": {
                                "fontSize": "0.914em"
                            }
                        };
                }
            }
        }
    }

    bocAmchart4.setValetDataLoader(chartSettings.bocSettings.args, chartSettings);

    if (chartSettings.bocSettings.args.hasOwnProperty("maxtooltipdistance")) {
        chartSettings.cursor.maxTooltipDistance = parseInt(chartSettings.bocSettings.args.maxtooltipdistance);
    }

    if (chartSettings.bocSettings.args.hasOwnProperty("disablecursor")
        && chartSettings.bocSettings.args.disablecursor === "true") {
        chartSettings.cursor = null;
    }

    if (chartSettings.hasOwnProperty("xAxes")) {
        if (chartSettings.bocSettings.args.hasOwnProperty("categoryaxistitle")) {
            chartSettings.xAxes[0].title = {
                text: chartSettings.bocSettings.args.categoryaxistitle,
                wrap: true,
                textAlign: "middle",
                maxWidth: 800,
                fontWeight: 600
            };
        } else {
            if (chartSettings.bocSettings.args.hasOwnProperty("bottomaxistitle")) {
                chartSettings.xAxes[0].title = {
                    text: chartSettings.bocSettings.args.bottomaxistitle,
                    wrap: true,
                    textAlign: "middle",
                    maxWidth: 800,
                    fontWeight: 600
                };
            }
        }
    }

    if (chartSettings.bocSettings.args.hasOwnProperty("zoomcontrol")) {
        if (chartSettings.bocSettings.args.zoomcontrol === "true") {
            chartSettings.zoomControl = {
                "align": "left",
                "valign": "middle",
                "scale": 0.75,
                "slider": {
                    "height": 250
                }
            };
        }
    }

    if (chartSettings.hasOwnProperty("yAxes")) {
        if (chartSettings.bocSettings.args.hasOwnProperty("rightaxistitle")) {
            chartSettings.yAxes[1].title = {
                text: chartSettings.bocSettings.args.rightaxistitle,
                wrap: true,
                maxWidth: 300,
                textAlign: "middle",
                rotation: -90,
                fontWeight: 600
            };
        }
        if (chartSettings.bocSettings.args.hasOwnProperty("leftaxistitle")) {
            chartSettings.yAxes[0].title = {
                text: chartSettings.bocSettings.args.leftaxistitle,
                wrap: true,
                maxWidth: 300,
                textAlign: "middle",
                rotation: -90,
                fontWeight: 600
            };
        }
    }

    if (chartSettings.bocSettings.args.hasOwnProperty('heatmap')) {
        chartSettings.cursor = {
            "type": "XYCursor",
            "behavior": "zoomX"
        };
    }


    if ((chartSettings.bocSettings.args.hasOwnProperty("hidelegend")
            && chartSettings.bocSettings.args.hidelegend === "true")
        || chartSettings.bocSettings.type === 'map' || chartSettings.bocSettings.args.hasOwnProperty("heatmap")
    ) {
        // No legend - nothing to do here
        delete chartSettings.legend;
    } else {
        let legendContainer = am4core.create(chartSettings.bocSettings.id + "-legend", am4core.Container);
        legendContainer.width = am4core.percent(100);
        legendContainer.height = am4core.percent(100);
        legendContainer.paddingRight = 30;

        chartSettings.legend.parent = legendContainer;
        chartSettings.legend.labels = {
            'wrap': true,
            'truncate': false,
            'fontSize': "0.875em"
        };

        if (chartLanguage === "fr") {
            chartSettings.legend.numberFormatter = {
                "language": {
                    "type": "Language",
                    "locale": {
                        "_decimalSeparator": ",",
                        "_thousandSeparator": " "
                    }
                }
            };
        }

        /**
         * It's possible to extend this list as long as the parameters follow the same pattern and
         * are available as direct parameters on legend, exploratory
         */
        let legendAttributes = [
            "truncateLabels",
            "switchable",
            "reverseOrder"
        ];

        let legendModified = false;

        legendAttributes.forEach(function (attr) {
            // AMCharts converts the properties to lowercase
            let legendProp = "legend" + attr.toLowerCase();
            if (chartSettings.bocSettings.args.hasOwnProperty(legendProp)) {
                switch (attr) {
                    case "switchable":
                        if (chartSettings.bocSettings.args[legendProp] === "false") {
                            chartSettings.legend.itemContainers = {
                                "clickable": false,
                                "focusable": false
                            };
                        }
                        break;
                    case "truncateLabels":
                        if (chartSettings.bocSettings.args[legendProp] === 'true') {
                            chartSettings.legend.labels.truncate = true;
                            chartSettings.legend.labels.wrap = false;
                        }
                        break;
                    case "reverseOrder":
                        if (chartSettings.bocSettings.args[legendProp] === 'true') {
                            chartSettings.legend.reverseOrder = true;
                        }
                        break;
                }

                if (bocAmchart4.debug(chartSettings)) {
                    // eslint-disable-next-line no-console
                    console.log(
                        chartSettings.bocSettings.id,
                        "Setting legend " + attr + " based on shortcode: ",
                        chartSettings.bocSettings.args[legendProp]
                    );
                }
                legendModified = true;
            }
        });

        if (legendModified && bocAmchart4.debug(chartSettings)) {
            // eslint-disable-next-line no-console
            console.log(
                chartSettings.bocSettings.id,
                "Modified legend: ",
                bocAmchart4.debugClone(chartSettings.legend)
            );
        }
    }

    if (chartSettings.bocSettings.args.hasOwnProperty("earlycallback")) {
        if (typeof window[chartSettings.bocSettings.args.earlycallback] === "function") {
            if (bocAmchart4.debug(chartSettings)) {
                // eslint-disable-next-line no-console
                console.groupCollapsed(chartSettings.bocSettings.id + " running earlycallback " + chartSettings.bocSettings.args.earlycallback);
                // eslint-disable-next-line no-console
                console.log(window[chartSettings.bocSettings.args.earlycallback].toString());
                // eslint-disable-next-line no-console
                console.groupEnd();
            }
            window[chartSettings.bocSettings.args.earlycallback](chartSettings);
        } else {
            // eslint-disable-next-line no-console
            console.log(chartSettings.bocSettings.id + " did not find early callback function " + chartSettings.bocSettings.args.earlycallback);
        }
    }

    if (chartSettings.bocSettings.args.hasOwnProperty('accent') &&
        chartSettings.bocSettings.args['accent'] === 'true') {
        let accentDiv = "<div class='bocss-chart-accent'><div class='bocss-accent-bar-top' aria-hidden='true'></div><div class='bocss-accent-bar' aria-hidden='true'></div></div>";
        let chartDiv = document.getElementById(chartSettings.bocSettings.id + 'p');
        chartDiv.insertAdjacentHTML('beforebegin', accentDiv);
    }

    if (bocAmchart4.debug(chartSettings)) {
        // eslint-disable-next-line no-console
        console.log(
            chartSettings.bocSettings.id,
            "After applyBosSettings for",
            bocAmchart4.debugClone(chartSettings)
        );
    }

    return chartSettings;
};


/**
 * Initialize the chart specific variables container based on chart's id
 * @param {AMConfig} chartSettings The chart options
 * @returns {void}
 */
bocAmchart4.initChartVars = function (chartSettings) {
    if (!chartSettings.bocSettings.id) {
        // eslint-disable-next-line no-console
        console.log("Chart id not set for chart:", chartSettings.titles);
    }
    bocAmchart4.vars = bocAmchart4.vars || {};
    bocAmchart4.vars[chartSettings.bocSettings.id] = bocAmchart4.vars[chartSettings.bocSettings.id] || {};
};

/**
 * Adds in padding values to the chart's config based on defined parameter values
 * @param {AMConfig} chartSettings The chart options
 * @param {string} direction The direction of the padding
 * @returns {void}
 */
bocAmchart4.addPadding = function (chartSettings, direction) {
    const paddingDirection = `padding${direction.toLowerCase()}`;
    if (chartSettings.bocSettings.args.hasOwnProperty(paddingDirection)) {
        chartSettings[`padding${direction}`] = parseInt(chartSettings.bocSettings.args[paddingDirection]);
    } else if (chartSettings.bocSettings.args['bottomaxisunit'] === ' %' && !chartSettings.bocSettings.args.hasOwnProperty('paddingright')) {
        chartSettings[`paddingRight`] = 20;
    }
};

/**
 * Applies label wrapping based on the chart's content width and specified maximum label width.
 *
 * @param {number} labelMaxWidth Maximum width for labels before wrapping.
 * @param {object} chart Chart object (passed as ev.target from event).
 * @returns {void}
 */
bocAmchart4.applyLabelWrapping = function (labelMaxWidth, chart) {
    setTimeout(function () {
        let labelsTemplate = chart.yAxes.getIndex(0).renderer.labels.template;
        labelsTemplate.wrap = true;
        if (chart.contentWidth > labelMaxWidth * 1.5) {
            labelsTemplate.maxWidth = labelMaxWidth;
        } else {
            labelsTemplate.maxWidth = chart.contentWidth * 0.66;
        }
    }, 10);
};
